import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../lib/cn';

export interface SwitchProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value'> {
  /** Floating label rendered on the field's top edge (matches TextInput). */
  label?: ReactNode;
  /** Label shown when the switch is on. Defaults to `Enabled`. */
  enabledLabel?: ReactNode;
  /** Label shown when the switch is off. Defaults to `Disabled`. */
  disabledLabel?: ReactNode;
  /** When true, renders the field in red. */
  error?: boolean;
  /** Helper / error message shown beneath the field. */
  errorMessage?: ReactNode;
}

/**
 * Boolean switch styled to line up with `<TextInput>` and `<SelectInput>`
 * inside FormSection grids. Lifted from My.Tms.Old/components/Switch.tsx.
 *
 * The visible labels (`enabledLabel` / `disabledLabel`) double as the
 * field's value display so callers don't need to render their own copy.
 */
export const Switch = forwardRef<HTMLInputElement, SwitchProps>(function Switch(
  {
    label,
    id,
    className,
    checked,
    enabledLabel = 'Enabled',
    disabledLabel = 'Disabled',
    error = false,
    errorMessage,
    ...props
  },
  ref,
) {
  const reactId = useId();
  const switchId = id ?? props.name ?? reactId;

  return (
    <div className="w-full">
      <div className="relative">
        <div
          className={cn(
            'peer flex h-9 w-full items-center justify-between rounded-sm border bg-white px-2 pb-1 pt-2',
            error
              ? 'border-red-500 focus-within:ring-2 focus-within:ring-red-500'
              : 'border-slate-300 focus-within:ring-2 focus-within:ring-blue-500',
            className,
          )}
        >
          <span className="text-sm font-semibold text-slate-900">
            {checked ? enabledLabel : disabledLabel}
          </span>
          <div className="relative inline-flex shrink-0 items-center">
            <input
              ref={ref}
              type="checkbox"
              id={switchId}
              className="peer sr-only"
              checked={checked}
              {...props}
            />
            <label
              htmlFor={switchId}
              className={cn(
                'relative flex h-5 w-9 cursor-pointer items-center rounded-full transition-colors duration-200 ease-in-out',
                checked ? 'bg-blue-600' : 'bg-slate-300',
                error && 'ring-2 ring-red-500',
              )}
            >
              <span
                className={cn(
                  'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200 ease-in-out',
                  checked ? 'translate-x-4' : 'translate-x-0.5',
                )}
              />
            </label>
          </div>
        </div>
        {label && (
          <label
            htmlFor={switchId}
            className={cn(
              'absolute -top-2 left-3 bg-white px-1 text-xs text-slate-500',
              error && 'peer-focus-within:text-red-500',
            )}
          >
            {label}
          </label>
        )}
      </div>
      {error && errorMessage && <p className="mt-1 text-xs text-red-600">{errorMessage}</p>}
    </div>
  );
});
